<template>
	<v-card>

		<div class="pa-2">
			<div class="mb-3 text-center ao-p2-regular">
				{{ $t('Setting the start time of work') }}
			</div>
			<p class="ao-p4-regular ao-text-800">
				{{ $t('You can set the start of the working day. This time will be used by default when selecting periods in forms, filters, maps and reports. You can always adjust it manually.') }}
			</p>
		</div>

		<div class="pa-2">
			<div class="text-center">
				<ao-text-field
					v-model="time"
					:label="$t('Start of day time')"
					variant="underlined"
					type="time"
					hideDetails
					class="w-50 mx-auto"
				/>
			</div>
			<ao-checkbox
				v-if="permissionToCloneConfigRights"
				v-model="isNeedCloneConfig"
				color="secondary"
				class="w-100"
			>
				{{ $t('Apply to all users of the company') }}
			</ao-checkbox>
		</div>

		<v-card-actions>
			<ao-btn
				class="flex-grow-1"
				color="primary"
				variant="flat"
				:loading="loadingPersonalCompanySettingsStatusByKey(PERSONAL_SETTINGS_MAPPING.START_DAY)"
				@click="onClickApply"
			>
				{{ $t('Apply') }}
			</ao-btn>

			<div class="px-1" />

			<ao-btn
				class="flex-grow-1"
				variant="text"
				@click="onClickCancel"
			>
				{{ $t('Cancel') }}
			</ao-btn>
		</v-card-actions>
	</v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'

import useMoment from '@/composables/useMoment.js'

import usePersonalCompanySettingsManager from '@/composables/managers/usePersonalCompanySettingsManager'

import usePresets from '@/composables/data/usePresets'

import { useUserStore } from '@/stores/userStore'
import { useCompanyStore } from '@/stores/companyStore'

import { PERSONAL_SETTINGS_MAPPING, PERSONAL_SETTINGS_TO_PRESET_CODE, PERSONAL_SETTINGS_TO_PRESET_CONFLICT_POLICY } from '@/config/personalSettingsConfig'

const emit = defineEmits({
	cancel: null,
})

const {
	moment,
} = useMoment()

const {
	updateStartDaySetting,
	personalCompanySettingStartDay,
} = usePersonalCompanySettingsManager()

const {
	cloneToCompanyPreset,
} = usePresets()

const userStore = useUserStore()
const { isSuperuserOrStaff } = storeToRefs(userStore)

const companyStore = useCompanyStore()
const {
	loadingPersonalCompanySettingsStatusByKey,
	currentUserIsCompanyOwner,
	hasViewEditDeleteManagePermission,
} = storeToRefs(companyStore)

const permissionToCloneConfigRights = computed(() => {
	return isSuperuserOrStaff.value || currentUserIsCompanyOwner.value || hasViewEditDeleteManagePermission.value
})

const time = ref('00:00')
const isNeedCloneConfig = ref(true)

const onClickApply = () => {
	const [hours, minutes] = time.value.split(':')
	const seconds = (hours * 60 * 60) + (minutes * 60)
	updateStartDaySetting(seconds)
		.then((res) => {
			if (permissionToCloneConfigRights.value && isNeedCloneConfig.value) {
				cloneToCompanyPreset(PERSONAL_SETTINGS_TO_PRESET_CODE[PERSONAL_SETTINGS_MAPPING.START_DAY], res.id, PERSONAL_SETTINGS_TO_PRESET_CONFLICT_POLICY[PERSONAL_SETTINGS_MAPPING.START_DAY])
				emit('cancel')
			} else {
				emit('cancel')
			}
		})
}

const onClickCancel = () => {
	emit('cancel')
}

onMounted(() => {
	try {
		const startTimeOfDay = personalCompanySettingStartDay.value
		time.value = moment.unix(startTimeOfDay).utc().format('HH:mm')
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error)
	}
})
</script>
